import { useOptionalUser } from '@/pages/user/selectors'
import { Match, Team } from '@/types'
import { useCallback } from 'react'
import { useProfileViews, useProfileViewsLimited } from './useProfileViews'

/** Whether a limited viewer must see `playerId`'s rating as locked. */
function isLockedFor(
  limited: boolean,
  viewedIds: number[],
  selfId: number | undefined,
  playerId: number | undefined
): boolean {
  if (!limited || !playerId || playerId === selfId) return false
  return !viewedIds.includes(playerId)
}

function teamHasPlayer(team: Team | undefined, userId: number): boolean {
  return team?.player1?.id === userId || team?.player2?.id === userId
}

/** Whether `userId` played in `match` (participants always see every rating). */
function isUserInMatch(match: Match | undefined, userId: number | undefined): boolean {
  if (!userId || !match?.teams) return false
  return match.teams.some((team) => teamHasPlayer(team, userId))
}

/**
 * Predicate for hiding another player's numeric rating behind a lock.
 *
 * A limited user sees a lock in place of the rating for any player they have
 * not unlocked this window; their own rating and unlocked players' ratings are
 * always visible. Unlimited/ungranted/DUPR+ users are never locked.
 */
export function useRatingLock(): (playerId: number | undefined) => boolean {
  const limited = useProfileViewsLimited()
  const { viewedIds } = useProfileViews()
  const user = useOptionalUser()
  return useCallback(
    (playerId: number | undefined) => isLockedFor(limited, viewedIds, user?.id, playerId),
    [limited, viewedIds, user?.id]
  )
}

/**
 * Rating lock for a match's players. Same as {@link useRatingLock}, except a
 * user who took part in the match sees every player's rating — you can always
 * see the ratings of a match you played in.
 */
export function useMatchRatingLock(match: Match | undefined): (playerId: number | undefined) => boolean {
  const isRatingLocked = useRatingLock()
  const user = useOptionalUser()
  const userIsInMatch = isUserInMatch(match, user?.id)
  return useCallback(
    (playerId: number | undefined) => !userIsInMatch && isRatingLocked(playerId),
    [userIsInMatch, isRatingLocked]
  )
}
